import { isToday } from 'date-fns'
import { cn, formatDateTimeHuman, formatTimeHuman } from '@/lib/utils'
import { MapPin, X } from 'lucide-react'
import type { Order } from '@/types'
import type { OrderTypeConfig } from '@/lib/orderTypeSettings'
import { displayLabelForOrderType } from '@/lib/orderTypeSettings'
import { orderTypePillLook, ticketLifecyclePillLook, type TicketLifecycleMeta } from './ticketState'

export interface TicketHeaderProps {
  orderType: string
  /** Resolved labels/colors for order-type pills */
  orderTypesConfig: OrderTypeConfig[]
  existingOrder: Order | null
  customerName: string
  /** Live delivery destination (delivery orders) — shown under the identity row. */
  deliveryAddress?: string
  lifecycle: TicketLifecycleMeta
  /** When provided, render an × icon button on the right to dismiss the loaded order. */
  onCloseOrder?: () => void
  /** Whether anything is currently "loaded" — controls × visibility. */
  hasLoadedOrder?: boolean
}

/**
 * Slim ticket header. Contextual actions live in `CounterRailToolbar` directly
 * below this header so the header can stay one tight block of identity
 * (order type, lifecycle, customer, order #, opened time).
 */
export function TicketHeader({
  orderType,
  orderTypesConfig,
  existingOrder,
  customerName,
  deliveryAddress,
  lifecycle,
  onCloseOrder,
  hasLoadedOrder,
}: TicketHeaderProps) {
  const orderNumber = existingOrder?.order_number

  // When the order was placed — the open-orders strip carries orders from
  // previous business days, so the date is included when it wasn't today.
  const openedAt = existingOrder?.created_at

  // Delivery destination — prefer the persisted address once the order exists,
  // else the live draft so it shows while the cashier is still composing.
  const deliveryAddr =
    orderType === 'delivery'
      ? (existingOrder?.delivery_address || deliveryAddress || '').trim()
      : ''

  const secondaryBits: string[] = []
  if (openedAt) {
    // The open-orders strip carries orders from previous business days;
    // a bare "10:35 PM" on a two-day-old ticket is misleading, so include
    // the date whenever the order wasn't opened today.
    const openedDate = new Date(openedAt)
    secondaryBits.push(
      `Opened ${isToday(openedDate) ? formatTimeHuman(openedDate) : formatDateTimeHuman(openedDate)}`
    )
  }

  // Suppress redundant helper text when it merely restates the lifecycle pill
  // ("Staging" + "Items not yet sent to the kitchen" was duplicative); keep
  // helpers that add real context (e.g. "Awaiting payment").
  const helperText = lifecycle.helper
  const helperRedundant =
    lifecycle.state === 'staging' &&
    helperText.toLowerCase().includes('not yet placed')
  const showHelper = Boolean(helperText) && !helperRedundant

  const otPill = orderTypePillLook(orderType, orderTypesConfig)
  const lifePill = ticketLifecyclePillLook(lifecycle.tone, orderType, orderTypesConfig)

  const showCloseButton = Boolean(onCloseOrder && hasLoadedOrder)

  return (
    <header
      aria-label="Ticket context"
      className="flex flex-col gap-1.5 border-b border-border/60 bg-card/95 px-4 py-2.5 backdrop-blur-sm sm:px-5"
    >
      <div className="flex items-start gap-2 min-w-0">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={cn(otPill.className)} style={otPill.style}>
              {displayLabelForOrderType(orderType, orderTypesConfig)}
            </span>
            <span className={cn(lifePill.className)} style={lifePill.style}>
              {lifecycle.label}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            {customerName ? (
              <span className="truncate text-base font-semibold tracking-tight">
                {customerName}
              </span>
            ) : (
              <span className="text-sm text-muted-foreground">Walk-in customer</span>
            )}
            {orderNumber && (
              <span className="text-sm text-muted-foreground">
                Order <span className="font-medium text-foreground">#{orderNumber}</span>
              </span>
            )}
          </div>
          {deliveryAddr && (
            <div className="mt-1 flex items-start gap-1 text-xs text-muted-foreground">
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="min-w-0 break-words">{deliveryAddr}</span>
            </div>
          )}
          {secondaryBits.length > 0 && (
            <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-muted-foreground">
              {secondaryBits.map((bit, i) => (
                <span key={`${bit}-${i}`}>
                  {i > 0 ? <span aria-hidden>· </span> : null}
                  {bit}
                </span>
              ))}
            </div>
          )}
          {showHelper && (
            <div className="mt-0.5 text-xs text-muted-foreground">{helperText}</div>
          )}
        </div>
        {showCloseButton && (
          <button
            type="button"
            onClick={onCloseOrder}
            className="-mr-1 -mt-1 shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Close this order"
            title="Close this order"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>
    </header>
  )
}
